// FZ-CARD (2026-07-13) — Cartões de crédito: fatura por ciclo + compras
// (histórico vem do sync CA; manuais pós-CA nascem aqui, parcelado "n/N").
// Espelha a tela de cartão do Conta Azul: seletor de cartão, navegação por mês,
// fechamento/vencimento/valor da fatura, lista de compras, fatura paga.
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Card, Button, Input, InputNumber, Select, Tag, Modal, Typography, Space,
  Table, App as AntApp, Flex, Empty, Spin, Switch,
} from 'antd';
import {
  PlusOutlined, SettingOutlined, LeftOutlined, RightOutlined,
  UploadOutlined, CheckCircleOutlined, TagsOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../config';
import { fmtDataBr, hojeIso } from '../utils/dataFmt';
import { getToken, fmt } from './financeiro-extrato/utils';
import { ModalNovoCartao } from './financeiro-cartoes/ModalNovoCartao';

const { Title, Text } = Typography;

// Soma delta meses a 'YYYY-MM'.
function addMesYYYYMM(mes: string, delta: number): string {
  const [y, m] = mes.split('-').map(Number);
  const tot = y * 12 + (m - 1) + delta;
  return `${Math.floor(tot / 12)}-${String((tot % 12) + 1).padStart(2, '0')}`;
}
// Sugere o MÊS da 1ª fatura a partir da data da compra + fechamento/vencimento
// do cartão (espelha o cálculo do backend). Compra até o fechamento cai no
// próprio ciclo; depois, no seguinte. Venc ≤ fech desloca +1 mês.
function sugerirVencMes(dataCompra: string, diaFech: number | null, diaVenc: number | null): string {
  if (!dataCompra || !diaFech || !diaVenc) return dataCompra ? dataCompra.slice(0, 7) : '';
  const diaCompra = parseInt(dataCompra.slice(8, 10), 10);
  const offset1 = diaCompra <= diaFech ? 0 : 1;
  const offsetVenc = diaVenc <= diaFech ? 1 : 0;
  return addMesYYYYMM(dataCompra.slice(0, 7), offset1 + offsetVenc);
}

interface Cartao {
  id: string; nome: string; banco: string | null; bandeira: string | null;
  final_cartao: string | null; conta_pagamento_nome: string | null;
  dia_fechamento: number | null; dia_vencimento: number | null; ativo: boolean;
}
interface CompraFatura {
  id: string; data_compra: string | null; descricao: string;
  categoria_dre: string | null; valor: number; observacoes: string | null;
  fonte: 'ca' | 'manual' | 'ca_aberto'; pago: boolean;
  // lancamentos_pagar.origem — separa o que veio do Conta Azul do que nasceu
  // na Nova despesa (os dois caem no ramo 'ca_aberto' da fatura).
  origem_lancamento?: string | null;
}
interface Fatura {
  cartao: { id: string; nome: string; dia_fechamento: number | null; dia_vencimento: number | null };
  mes: string; fechamento: string | null; vencimento: string | null;
  valor_total: number;
  pagamento: { id: string; data: string; valor: number; origem_conta_nome: string } | null;
  compras: CompraFatura[];
}

// hojeIso() é LOCAL (não UTC) — toISOString viraria o dia seguinte após 21h BRT.
function mesAtual() { return hojeIso().slice(0, 7); }
function addMes(mes: string, delta: number) {
  const [y, m] = mes.split('-').map(Number);
  const t = y * 12 + (m - 1) + delta;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
}
function rotuloMes(mes: string) {
  const nomes = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  const [y, m] = mes.split('-').map(Number);
  return `${nomes[m - 1]} de ${y}`;
}

function FinanceiroCartoesInner() {
  const { message } = AntApp.useApp();
  const [cartoes, setCartoes] = useState<Cartao[]>([]);
  const [cartaoId, setCartaoId] = useState<string>('');
  const [mes, setMes] = useState<string>(mesAtual());
  const [fatura, setFatura] = useState<Fatura | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingFatura, setLoadingFatura] = useState(false);
  const [modalCompra, setModalCompra] = useState(false);
  const [modalConfig, setModalConfig] = useState(false);
  const [modalNovo, setModalNovo] = useState(false);
  const [modalImport, setModalImport] = useState(false);
  const [modalPagar, setModalPagar] = useState(false);
  const [dataPagar, setDataPagar] = useState('');
  const [csvTexto, setCsvTexto] = useState('');
  const [linhasXlsx, setLinhasXlsx] = useState<any[] | null>(null);
  const [vencArquivo, setVencArquivo] = useState<string | null>(null);
  const [nomeArquivo, setNomeArquivo] = useState('');
  const [previaCsv, setPreviaCsv] = useState<any>(null);
  const [importando, setImportando] = useState(false);
  const [pagando, setPagando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [categoriasDre, setCategoriasDre] = useState<any[]>([]);

  const [formCompra, setFormCompra] = useState({
    descricao: '', valor_total: null as number | null, parcelas: 1,
    data_compra: hojeIso(),         // data específica = competência da despesa
    primeiro_vencimento_mes: '',    // YYYY-MM da 1ª fatura (sugerido pelo fechamento)
    categoria_dre: '', grupo_dre: '', fornecedor_original: '', observacoes: '',
  });
  const [formConfig, setFormConfig] = useState({
    dia_fechamento: null as number | null, dia_vencimento: null as number | null,
    bandeira: '', final_cartao: '', conta_pagamento_nome: '', ativo: true,
  });
  // Contas correntes ativas — quem pode pagar a fatura (o servidor confere).
  const [correntes, setCorrentes] = useState<{ nome: string; apelido: string | null }[]>([]);

  const carregarCartoes = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const [rC, rCat] = await Promise.all([
        axios.get(`${API_URL}/financeiro/cartoes`, { headers: { Authorization: token } }),
        axios.get(`${API_URL}/categorias`, { headers: { Authorization: token } }).catch(() => ({ data: [] })),
      ]);
      const d = rC.data.body ? JSON.parse(rC.data.body) : rC.data;
      const lista: Cartao[] = d.cartoes || [];
      setCartoes(lista);
      const dCat = (rCat as any).data.body ? JSON.parse((rCat as any).data.body) : (rCat as any).data;
      setCategoriasDre(Array.isArray(dCat) ? dCat.filter((c: any) => c.linha_dre) : []);
      if (lista.length) {
        // Set funcional (não lê cartaoId do closure — deps []): mantém a seleção
        // atual se ela ainda existe; senão cai no primeiro ativo.
        setCartaoId(prev => (prev && lista.some(c => c.id === prev))
          ? prev
          : (lista.find(c => c.ativo) || lista[0]).id);
      }
    } catch (e: any) {
      message.error('Erro carregando cartões: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sequencial pra descartar resposta fora de ordem (navegação rápida de mês:
  // a resposta antiga não pode sobrescrever a do mês atual).
  const faturaReqSeq = useRef(0);
  const carregarFatura = useCallback(async () => {
    if (!cartaoId) { setFatura(null); return; }
    const reqId = ++faturaReqSeq.current;
    setLoadingFatura(true);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/financeiro/cartoes/${cartaoId}/fatura?mes=${mes}`,
        { headers: { Authorization: token } });
      if (reqId !== faturaReqSeq.current) return;  // já tem requisição mais nova
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setFatura(d);
    } catch (e: any) {
      if (reqId !== faturaReqSeq.current) return;
      message.error('Erro carregando fatura: ' + (e.response?.data?.error || e.message));
      setFatura(null);
    } finally {
      if (reqId === faturaReqSeq.current) setLoadingFatura(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartaoId, mes]);

  useEffect(() => { carregarCartoes(); }, [carregarCartoes]);
  useEffect(() => { carregarFatura(); }, [carregarFatura]);

  const cartao = cartoes.find(c => c.id === cartaoId) || null;

  async function abrirConfig() {
    if (!cartao) return;
    setFormConfig({
      dia_fechamento: cartao.dia_fechamento, dia_vencimento: cartao.dia_vencimento,
      bandeira: cartao.bandeira || '', final_cartao: cartao.final_cartao || '',
      conta_pagamento_nome: cartao.conta_pagamento_nome || '', ativo: cartao.ativo !== false,
    });
    setModalConfig(true);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/financeiro/contas`, { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      setCorrentes((Array.isArray(d) ? d : []).filter((c: any) => c.tipo === 'corrente' && c.ativo !== false));
    } catch { /* sem a lista, o servidor ainda confere — o erro aparece ao salvar */ }
  }

  async function salvarConfig() {
    if (!cartao) return;
    setSalvando(true);
    try {
      const token = await getToken();
      // `ativo` e a conta que paga só vão se mudaram: desativar tem trava no
      // servidor (compra por pagar) e a conta é conferida contra as correntes
      // ativas — mexer só no vencimento não pode esbarrar numa conta antiga.
      const { ativo, conta_pagamento_nome, ...resto } = formConfig;
      const corpo: any = { ...resto };
      if (ativo !== (cartao.ativo !== false)) corpo.ativo = ativo;
      if ((conta_pagamento_nome || '') !== (cartao.conta_pagamento_nome || '')) corpo.conta_pagamento_nome = conta_pagamento_nome;
      await axios.patch(`${API_URL}/financeiro/cartoes/${cartao.id}`, corpo,
        { headers: { Authorization: token } });
      message.success('Configuração salva.');
      setModalConfig(false);
      await carregarCartoes(); await carregarFatura();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function salvarCompra() {
    if (!cartao) return;
    if (!formCompra.descricao.trim() || !formCompra.valor_total) {
      message.warning('Preencha descrição e valor'); return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/financeiro/cartoes/${cartao.id}/compras`, formCompra,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.success(`Compra lançada (${d.parcelas} parcela${d.parcelas > 1 ? 's' : ''}).`);
      setModalCompra(false);
      setFormCompra(f => ({ ...f, descricao: '', valor_total: null, parcelas: 1, observacoes: '' }));
      await carregarFatura();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function pagarFatura() {
    if (!cartao || !fatura) return;
    setPagando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/financeiro/cartoes/${cartao.id}/fatura/pagar`,
        { mes, valor: fatura.valor_total, data_pagamento: dataPagar || undefined },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      message.success(`Fatura paga: ${d.baixadas} compra(s) baixada(s)`
        + (d.transferencia_criada ? ' + transferência banco→cartão registrada.' : '.'));
      setModalPagar(false);
      await carregarFatura();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setPagando(false); }
  }

  // XLSX do Itaú: layout de RELATÓRIO (resumo, fatura anterior, juros/CET),
  // não tabela. As transações vivem em blocos com cabeçalho data·descrição·valor
  // (colunas A/C/K no export atual — o cabeçalho localiza a coluna do valor, não
  // confiamos na posição). Compra vem POSITIVA no Itaú; normalizamos pra
  // convenção do motor (compra negativa, pagamento positivo) invertendo o sinal.
  function parseItauXlsx(wbook: XLSX.WorkBook) {
    const ws = wbook.Sheets[wbook.SheetNames[0]];
    const grade: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true });
    const linhas: any[] = [];
    let venc: string | null = null;
    let colValor = -1;
    const ehData = (v: any) => v instanceof Date && !isNaN(+v);
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    for (const row of grade) {
      if (!row || !row.length) continue;
      const c1 = row[0];
      // vencimento: linha de status da fatura ('fechada'/'aberta') com a data na col C
      if (typeof c1 === 'string' && /^(fechada|aberta)/i.test(c1.trim()) && ehData(row[2])) {
        const d = row[2] as Date;
        venc = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
        continue;
      }
      // cabeçalho de bloco de transações: acha a coluna do valor
      if (typeof c1 === 'string' && c1.trim().toLowerCase() === 'data') {
        colValor = row.findIndex((v: any) => typeof v === 'string' && v.trim().toLowerCase() === 'valor');
        continue;
      }
      if (colValor < 0 || !ehData(c1)) continue;
      const desc = String(row[2] ?? '').replace(/\s+/g, ' ').trim();
      const valor = parseFloat(row[colValor]);
      if (!desc || !Number.isFinite(valor) || valor === 0) continue;
      // parcela no fim da descrição ("PG *CPE COMERCIO D12/18" → 12/18)
      const mp = desc.match(/(\d{1,2})\/(\d{1,2})\s*$/);
      linhas.push({
        data: iso(c1 as Date),
        lancamento: mp ? desc.replace(/\s*D?\d{1,2}\/\d{1,2}\s*$/, '').trim() : desc,
        parcela: mp ? `${parseInt(mp[1], 10)}/${parseInt(mp[2], 10)}` : null,
        valor: -valor,                       // Itaú: compra positiva → convenção do motor
        categoria: null,
      });
    }
    return { linhas, venc };
  }

  function lerArquivoFatura(f: File) {
    setNomeArquivo(f.name);
    if (/\.xlsx?$/i.test(f.name)) {
      const rd = new FileReader();
      rd.onload = () => {
        try {
          const wbook = XLSX.read(rd.result, { type: 'array', cellDates: true });
          const { linhas, venc } = parseItauXlsx(wbook);
          if (!linhas.length) { message.warning('Nenhuma transação reconhecida — é o XLSX da fatura do Itaú?'); return; }
          setLinhasXlsx(linhas); setVencArquivo(venc); setCsvTexto('');
          message.success(`${linhas.length} transação(ões) reconhecida(s)${venc ? ` · vencimento ${venc}` : ''}.`);
        } catch (e: any) { message.error('Falha lendo o XLSX: ' + e.message); }
      };
      rd.readAsArrayBuffer(f);
    } else {
      const rd = new FileReader();
      rd.onload = () => { setCsvTexto(String(rd.result || '')); setLinhasXlsx(null); setVencArquivo(null); };
      rd.readAsText(f, 'utf-8');
    }
  }

  // Categoria por linha da prévia, chaveada por descrição|valor (mesma chave
  // que o backend usa pra casar) — o índice não serve: a tabela pode reordenar.
  // `categoriasDre` já é carregada no boot da página (junto com os cartões).
  const [catPorLinha, setCatPorLinha] = useState<Record<string, string>>({});
  const chaveLinha = (r: any) => `${r.descricao}|${Number(r.valor).toFixed(2)}`;

  // ── Classificar pendentes (fatura já importada) ───────────────────────────
  const [modalClassif, setModalClassif] = useState(false);
  const [classifLinhas, setClassifLinhas] = useState<any[]>([]);
  const [classifResumo, setClassifResumo] = useState<any>(null);
  const [classifCat, setClassifCat] = useState<Record<string, string>>({});
  const [classificando, setClassificando] = useState(false);

  async function abrirClassificar() {
    if (!cartao) return;
    setModalClassif(true); setClassifLinhas([]); setClassifResumo(null); setClassifCat({});
    setClassificando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/financeiro/cartoes/${cartao.id}/fatura/classificar`,
        { mes }, { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      setClassifLinhas(d.linhas || []); setClassifResumo(d.resumo || null);
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setClassificando(false); }
  }

  async function aplicarClassificacao() {
    if (!cartao) return;
    // Vale a escolha da pessoa; onde ela não mexeu, vale a sugestão exibida.
    const categorias = classifLinhas
      .map((l: any) => ({ id: l.id, categoria: classifCat[l.id] ?? l.categoria_sugerida ?? null }))
      .filter((x: any) => x.categoria);
    if (!categorias.length) { message.warning('Nenhuma categoria escolhida.'); return; }
    setClassificando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/financeiro/cartoes/${cartao.id}/fatura/classificar`,
        { mes, confirmar: true, categorias }, { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      message.success(`${d.atualizadas} lançamento(s) classificado(s).`);
      setModalClassif(false);
      await carregarFatura();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setClassificando(false); }
  }

  async function chamarImport(confirmar: boolean) {
    if (!cartao || (!csvTexto.trim() && !linhasXlsx)) return;
    setImportando(true);
    try {
      const token = await getToken();
      // Na confirmação, manda as categorias REVISADAS (a sugestão que a pessoa
      // não mexeu vale como aprovada — é o que ela vê na tela).
      const categorias = confirmar
        ? (previaCsv?.linhas || []).filter((l: any) => l.situacao === 'criar')
            .map((l: any) => ({ descricao: l.descricao, valor: l.valor,
              categoria: catPorLinha[chaveLinha(l)] ?? l.categoria_sugerida ?? null }))
            .filter((x: any) => x.categoria)
        : undefined;
      const r = await axios.post(`${API_URL}/financeiro/cartoes/${cartao.id}/fatura/importar-csv`,
        linhasXlsx
          ? { mes, linhas: linhasXlsx, vencimento_arquivo: vencArquivo || undefined, confirmar, categorias }
          : { mes, csv: csvTexto, confirmar, categorias },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      if (confirmar) {
        message.success(`${d.criadas} compra(s) criada(s)${d.categorizadas ? ` · ${d.categorizadas} já categorizada(s)` : ''}.`);
        setModalImport(false); setCsvTexto(''); setPreviaCsv(null); setCatPorLinha({});
        await carregarFatura();
      } else {
        setPreviaCsv(d);
        setCatPorLinha({});
      }
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setImportando(false); }
  }

  const cols: ColumnsType<CompraFatura> = [
    {
      title: 'Data da compra', dataIndex: 'data_compra', key: 'd', width: 120,
      render: (v) => <Text style={{ fontSize: 12 }}>{v ? fmtDataBr(v) : '—'}</Text>,
    },
    {
      title: 'Descrição', dataIndex: 'descricao', key: 'desc', ellipsis: { showTitle: false },
      render: (_, r) => (
        <span title={[r.descricao, r.observacoes].filter(Boolean).join('\n')}>
          <Text style={{ fontSize: 12 }}>{r.descricao}</Text>
        </span>
      ),
    },
    {
      title: 'Categoria', dataIndex: 'categoria_dre', key: 'cat', width: 220, ellipsis: true,
      render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{v || '—'}</Text>,
    },
    {
      title: 'Origem', dataIndex: 'fonte', key: 'f', width: 90, align: 'center',
      render: (v, r) => v === 'manual'
        ? <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>Sistema</Tag>
        : v === 'ca_aberto' && ['manual', 'reembolso_mobile'].includes(String(r.origem_lancamento || ''))
          // Despesa lançada no Contas a Pagar (Nova despesa) com o cartão de conta.
          ? <Tag color={r.pago ? 'green' : 'geekblue'} style={{ margin: 0, fontSize: 10 }}>
              {r.pago ? 'Contas a Pagar · pago' : 'Contas a Pagar'}
            </Tag>
        : v === 'ca_aberto'
          // O limbo pós-cut-over: veio do CA mas o sync morreu antes de baixar.
          // Laranja enquanto aberto; o "Pagar fatura" daqui é quem baixa.
          ? <Tag color={r.pago ? 'green' : 'orange'} style={{ margin: 0, fontSize: 10 }}>
              {r.pago ? 'CA · pago aqui' : 'CA · aguardando'}
            </Tag>
          : <Tag style={{ margin: 0, fontSize: 10 }}>Conta Azul</Tag>,
    },
    {
      title: 'Valor', dataIndex: 'valor', key: 'v', align: 'right', width: 120,
      render: (v) => <Text strong style={{ color: '#B91626', fontSize: 12 }}>{fmt(Math.abs(parseFloat(v as any) || 0) * -1)}</Text>,
    },
  ];

  if (loading) return <Flex justify="center" style={{ padding: 60 }}><Spin /></Flex>;

  return (
    <div style={{ padding: 16 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={12} style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>💳 Cartões de crédito</Title>
        <Space wrap>
          <Select
            style={{ minWidth: 280 }}
            value={cartaoId || undefined}
            placeholder="Selecione o cartão"
            onChange={setCartaoId}
            options={cartoes.map(c => ({
              value: c.id,
              label: `${c.nome}${c.ativo ? '' : ' (inativo)'}`,
            }))}
          />
          {/* Cartão novo pela tela (22/09/2026): antes só o "Sincronizar do CA"
              criava cartão, e o Conta Azul foi desligado em 15/07. */}
          <Button icon={<PlusOutlined />} onClick={() => setModalNovo(true)}>Novo cartão</Button>
          <Button icon={<SettingOutlined />} onClick={abrirConfig} disabled={!cartao}>Configurar</Button>
          <Button icon={<UploadOutlined />} disabled={!cartao}
            onClick={() => { setCsvTexto(''); setPreviaCsv(null); setLinhasXlsx(null); setVencArquivo(null); setNomeArquivo(''); setModalImport(true); }}>
            Importar CSV
          </Button>
          {/* Classifica o que JÁ está na fatura sem categoria — fatura
              importada antes do classificador, ou lançamento do CA sem
              categoria. Mesma revisão da prévia do import. */}
          <Button icon={<TagsOutlined />} disabled={!cartao} onClick={abrirClassificar}>
            Classificar pendentes
          </Button>
          <Button type="primary" icon={<PlusOutlined />} disabled={!cartao}
            onClick={() => {
              // sugere o mês da 1ª fatura pelo fechamento do cartão
              setFormCompra(f => ({ ...f,
                primeiro_vencimento_mes: sugerirVencMes(f.data_compra, cartao?.dia_fechamento ?? null, cartao?.dia_vencimento ?? null),
              }));
              setModalCompra(true);
            }}>
            Nova compra
          </Button>
        </Space>
      </Flex>

      {!cartoes.length && (
        <Empty description="Nenhum cartão cadastrado ainda. Clique em ‘Novo cartão’ para cadastrar." />
      )}

      {modalNovo && (
        <ModalNovoCartao
          onFechar={() => setModalNovo(false)}
          onSalvou={async (novo) => {
            setModalNovo(false);
            await carregarCartoes();
            // carregarCartoes mantém a seleção anterior; o cartão novo passa a ser o da tela.
            setCartaoId(novo.id);
          }}
        />
      )}

      {cartao && (
        <>
          <Flex justify="center" align="center" gap={8} style={{ marginBottom: 16 }}>
            <Button icon={<LeftOutlined />} size="small" onClick={() => setMes(m => addMes(m, -1))} />
            <Text strong style={{ minWidth: 160, textAlign: 'center' }}>{rotuloMes(mes)}</Text>
            <Button icon={<RightOutlined />} size="small" onClick={() => setMes(m => addMes(m, +1))} />
          </Flex>

          <Spin spinning={loadingFatura}>
            <Flex gap={12} wrap="wrap" style={{ marginBottom: 16 }}>
              <Card size="small" style={{ flex: 1, minWidth: 180 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Fechamento</Text>
                <div><Text strong>{fatura?.fechamento ? fmtDataBr(fatura.fechamento) : 'configure ⚙'}</Text></div>
              </Card>
              <Card size="small" style={{ flex: 1, minWidth: 180 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Vencimento</Text>
                <div><Text strong>{fatura?.vencimento ? fmtDataBr(fatura.vencimento) : 'configure ⚙'}</Text></div>
              </Card>
              <Card size="small" style={{ flex: 1, minWidth: 200 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Valor da fatura</Text>
                <div><Text strong style={{ fontSize: 16, color: '#B91626' }}>
                  {fmt((fatura?.valor_total || 0) * -1)}
                </Text></div>
              </Card>
              <Card size="small" style={{ flex: 1, minWidth: 220 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Pagamento</Text>
                <div>
                  {fatura?.pagamento ? (
                    <Tag color="success" style={{ margin: 0 }}>
                      ✓ paga em {fmtDataBr(fatura.pagamento.data)} · {fmt(Math.abs(parseFloat(fatura.pagamento.valor as any) || 0))}
                    </Tag>
                  ) : (
                    <Space size={6}>
                      <Tag style={{ margin: 0 }}>em aberto</Tag>
                      {(fatura?.compras || []).some(x => !x.pago) && (
                        <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                          loading={pagando}
                          onClick={() => {
                            // Data já nasce no VENCIMENTO configurado do cartão
                            // (é quando o débito automático paga) — clicar dias
                            // depois não suja a data real do fluxo de caixa.
                            setDataPagar(fatura?.vencimento || hojeIso());
                            setModalPagar(true);
                          }}>
                          Pagar fatura
                        </Button>
                      )}
                    </Space>
                  )}
                </div>
              </Card>
            </Flex>

            <Card size="small">
              <Table<CompraFatura>
                rowKey={(r) => `${r.fonte}_${r.id}`}
                columns={cols}
                dataSource={fatura?.compras || []}
                pagination={false}
                size="small"
                locale={{ emptyText: <Empty description="Nenhuma compra nesta fatura" /> }}
              />
            </Card>
          </Spin>
        </>
      )}

      <Modal
        title={`Nova compra — ${cartao?.nome || ''}`}
        open={modalCompra}
        onCancel={() => setModalCompra(false)}
        onOk={salvarCompra}
        okText="Lançar compra"
        confirmLoading={salvando}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>Descrição *</Text>
            <Input value={formCompra.descricao}
              onChange={e => setFormCompra({ ...formCompra, descricao: e.target.value })}
              placeholder="Ex: Assinatura software X" />
          </div>
          <Flex gap={10} wrap="wrap">
            <div style={{ flex: 1, minWidth: 140 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Valor total (R$) *</Text>
              <InputNumber style={{ width: '100%' }} min={0.01} decimalSeparator=","
                value={formCompra.valor_total}
                onChange={v => setFormCompra({ ...formCompra, valor_total: v })} />
            </div>
            <div style={{ minWidth: 110 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Parcelas</Text>
              <Select style={{ width: '100%' }} value={formCompra.parcelas}
                onChange={v => setFormCompra({ ...formCompra, parcelas: v })}
                options={Array.from({ length: 24 }, (_, i) => ({ value: i + 1, label: i === 0 ? 'À vista' : `${i + 1}x` }))} />
            </div>
            <div style={{ minWidth: 150 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Data da compra (competência) *</Text>
              <Input type="date" value={formCompra.data_compra}
                onChange={e => setFormCompra(f => ({
                  ...f, data_compra: e.target.value,
                  // re-sugere o mês da 1ª fatura ao trocar a data da compra
                  primeiro_vencimento_mes: sugerirVencMes(e.target.value, cartao?.dia_fechamento ?? null, cartao?.dia_vencimento ?? null),
                }))} />
            </div>
            <div style={{ minWidth: 150 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>1º vencimento (fatura)</Text>
              <Input type="month" value={formCompra.primeiro_vencimento_mes}
                onChange={e => setFormCompra({ ...formCompra, primeiro_vencimento_mes: e.target.value })} />
            </div>
          </Flex>
          <Text type="secondary" style={{ fontSize: 11 }}>
            A <b>data da compra</b> é a competência (quando a despesa é reconhecida na DRE).
            O <b>1º vencimento</b> é o mês em que a 1ª parcela cai na fatura — sugerido pelo
            fechamento do cartão{cartao?.dia_fechamento ? ` (fecha dia ${cartao.dia_fechamento})` : ''}, editável.
          </Text>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>Categoria na DRE</Text>
            <Select style={{ width: '100%' }} allowClear showSearch
              value={formCompra.categoria_dre || undefined}
              placeholder="Selecione a categoria"
              onChange={v => {
                const c = categoriasDre.find((c: any) => c.linha_dre === v);
                setFormCompra({ ...formCompra, categoria_dre: v || '', grupo_dre: c?.grupo_dre || '' });
              }}
              options={categoriasDre.map((c: any) => ({ value: c.linha_dre, label: c.linha_dre }))} />
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>Fornecedor</Text>
            <Input value={formCompra.fornecedor_original}
              onChange={e => setFormCompra({ ...formCompra, fornecedor_original: e.target.value })}
              placeholder="(opcional)" />
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>Observações</Text>
            <Input.TextArea rows={2} value={formCompra.observacoes}
              onChange={e => setFormCompra({ ...formCompra, observacoes: e.target.value })} />
          </div>
          {formCompra.parcelas > 1 && formCompra.valor_total ? (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {formCompra.parcelas}x de ~{fmt(formCompra.valor_total / formCompra.parcelas)} — 1ª na fatura de{' '}
              {formCompra.primeiro_vencimento_mes
                ? formCompra.primeiro_vencimento_mes.split('-').reverse().join('/')
                : '—'}, as demais nos meses seguintes.
            </Text>
          ) : null}
        </Space>
      </Modal>

      <Modal
        title={`Configurar — ${cartao?.nome || ''}`}
        open={modalConfig}
        onCancel={() => setModalConfig(false)}
        onOk={salvarConfig}
        okText="Salvar"
        confirmLoading={salvando}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          <Flex gap={10}>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Dia de fechamento da fatura *</Text>
              <InputNumber style={{ width: '100%' }} min={1} max={31}
                value={formConfig.dia_fechamento}
                onChange={v => setFormConfig({ ...formConfig, dia_fechamento: v })} />
            </div>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Dia de vencimento *</Text>
              <InputNumber style={{ width: '100%' }} min={1} max={31}
                value={formConfig.dia_vencimento}
                onChange={v => setFormConfig({ ...formConfig, dia_vencimento: v })} />
            </div>
          </Flex>
          <Flex gap={10}>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Bandeira</Text>
              <Input value={formConfig.bandeira}
                onChange={e => setFormConfig({ ...formConfig, bandeira: e.target.value })}
                placeholder="MASTERCARD" />
            </div>
            <div style={{ width: 130 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Final do cartão</Text>
              <Input value={formConfig.final_cartao} inputMode="numeric"
                onChange={e => setFormConfig({ ...formConfig, final_cartao: e.target.value.replace(/[^0-9]/g, '').slice(-4) })}
                placeholder="3147" />
            </div>
          </Flex>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>Conta que paga a fatura</Text>
            {/* Era texto livre: um nome digitado diferente do cadastro deixava o
                pagamento da fatura sem conta e fora da conciliação. */}
            <Select style={{ width: '100%' }} allowClear placeholder="Escolha a conta corrente"
              value={formConfig.conta_pagamento_nome || undefined}
              onChange={v => setFormConfig({ ...formConfig, conta_pagamento_nome: v || '' })}
              options={[
                ...correntes.map(c => ({ value: c.nome, label: c.apelido || c.nome })),
                // conta gravada que já não está entre as correntes ativas: mostra, não apaga
                ...(formConfig.conta_pagamento_nome && !correntes.some(c => c.nome === formConfig.conta_pagamento_nome)
                  ? [{ value: formConfig.conta_pagamento_nome, label: `${formConfig.conta_pagamento_nome} (fora das correntes ativas)` }]
                  : []),
              ]} />
          </div>
          <Flex align="center" gap={10}>
            <Switch checked={formConfig.ativo} onChange={v => setFormConfig({ ...formConfig, ativo: v })} />
            <Text style={{ fontSize: 12.5 }}>
              {formConfig.ativo ? 'Cartão ativo' : 'Cartão desativado'}
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                Desativado some da Nova despesa e das listas. Só desativa sem compra por pagar.
              </Text>
            </Text>
          </Flex>
        </Space>
      </Modal>

      {/* Pagar fatura: modal próprio (não confirm) porque tem DATA editável e o
          aviso das sobras — as duas coisas que evitam sujar o caixa. */}
      <Modal
        title={`Pagar fatura — ${cartao?.nome || ''} · ${rotuloMes(mes)}`}
        open={modalPagar}
        onCancel={() => setModalPagar(false)}
        footer={[
          <Button key="c" onClick={() => setModalPagar(false)}>Cancelar</Button>,
          <Button key="ok" type="primary" loading={pagando} icon={<CheckCircleOutlined />}
            disabled={!dataPagar} onClick={pagarFatura}>
            Pagar {fmt(Math.abs(fatura?.valor_total || 0) * -1)}
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          {(() => {
            const abertas = (fatura?.compras || []).filter(x => !x.pago);
            // Sobras carimbadas pelo import: agendado do CA que o CSV não cobre
            // (valor estimado ≠ cobrado). Pagar sem resolver dobra a despesa.
            const sobras = abertas.filter(x => (x.observacoes || '').includes('não consta no CSV'));
            return (
              <>
                {sobras.length > 0 && (
                  <Card size="small" style={{ borderColor: '#ffa39e', background: '#fff1f0' }}>
                    <Text strong style={{ fontSize: 12, color: '#a8071a' }}>
                      ⚠ {sobras.length} lançamento(s) marcado(s) como fora do CSV importado — resolva antes de pagar
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', margin: '4px 0 6px' }}>
                      São agendados do Conta Azul cujo valor não bate com a fatura real. Se o import
                      criou o valor cobrado, cancele estes no Contas a Pagar — pagar assim marca os
                      dois como pagos e dobra a despesa do mês.
                    </Text>
                    {sobras.map(x => (
                      <div key={x.id} style={{ fontSize: 12 }}>
                        • {x.descricao} — <Text strong>{fmt(Math.abs(parseFloat(x.valor as any) || 0) * -1)}</Text>
                      </div>
                    ))}
                  </Card>
                )}
                <div>
                  Baixa <Text strong>{abertas.length}</Text> compra(s) em aberto e registra a
                  transferência <Text strong>{cartao?.conta_pagamento_nome || 'banco'}</Text> → <Text strong>{cartao?.nome}</Text>.
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>Data do pagamento</Text>
                  <Input type="date" value={dataPagar} style={{ width: 180, display: 'block' }}
                    onChange={e => setDataPagar(e.target.value)} />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Sugerida pelo vencimento do cartão (dia {cartao?.dia_vencimento || '—'}) — é a data
                    que vai pro fluxo de caixa; ajuste se o débito saiu em outro dia.
                  </Text>
                </div>
              </>
            );
          })()}
        </Space>
      </Modal>

      {/* Import da fatura CSV do Inter. Prévia SEMPRE antes de criar — regra da
          casa: conciliação sugere, humano confirma. */}
      <Modal
        title={`Importar fatura CSV — ${cartao?.nome || ''} · ${rotuloMes(mes)}`}
        open={modalImport}
        onCancel={() => { setModalImport(false); setPreviaCsv(null); setCsvTexto(''); setLinhasXlsx(null); setVencArquivo(null); setNomeArquivo(''); }}
        width="94vw" style={{ maxWidth: 900, top: 24 }}
        styles={{ body: { maxHeight: 'calc(100vh - 210px)', overflowY: 'auto' } }}
        footer={[
          <Button key="c" onClick={() => { setModalImport(false); setPreviaCsv(null); setCsvTexto(''); setLinhasXlsx(null); setVencArquivo(null); setNomeArquivo(''); }}>Cancelar</Button>,
          !previaCsv
            ? <Button key="p" type="primary" loading={importando} disabled={!csvTexto.trim() && !linhasXlsx}
                onClick={() => chamarImport(false)}>Ver prévia</Button>
            : <Button key="ok" type="primary" loading={importando}
                disabled={!previaCsv?.resumo?.criar}
                onClick={() => chamarImport(true)}>
                Criar {previaCsv?.resumo?.criar || 0} compra(s)
              </Button>,
        ]}
      >
        {!previaCsv && (
          <Space direction="vertical" style={{ width: '100%' }} size={10}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Selecione o arquivo da fatura: CSV do Inter ou XLSX do Itaú (ou cole o conteúdo do CSV).
              Nada é criado sem a prévia: o sistema casa cada linha com o que já está na fatura
              e só propõe criar o que falta.
            </Text>
            <input type="file" accept=".csv,.xlsx,text/csv"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) lerArquivoFatura(f); }} />
            {linhasXlsx && (
              <Tag color="blue" style={{ whiteSpace: 'normal' }}>
                {nomeArquivo}: {linhasXlsx.length} transação(ões) do Itaú prontas{vencArquivo ? ` · vencimento ${vencArquivo}` : ''}
              </Tag>
            )}
            <Input.TextArea rows={8} value={csvTexto} placeholder='"Descricao","Data","Cartao","Lançamento",…'
              onChange={e => setCsvTexto(e.target.value)} style={{ fontFamily: 'monospace', fontSize: 11 }} />
          </Space>
        )}
        {previaCsv && (
          <Space direction="vertical" style={{ width: '100%' }} size={10}>
            {previaCsv.aviso && <Tag color="red" style={{ whiteSpace: 'normal' }}>{previaCsv.aviso}</Tag>}
            <Space wrap>
              <Tag color="blue">criar: {previaCsv.resumo?.criar}</Tag>
              <Tag color="green">já existem: {previaCsv.resumo?.ja_existem}</Tag>
              <Tag>pagamentos (ignorados): {previaCsv.resumo?.pagamentos}</Tag>
              {previaCsv.resumo?.sugeridas > 0 && (
                <Tag color="purple">categoria sugerida: {previaCsv.resumo.sugeridas} de {previaCsv.resumo.criar}</Tag>
              )}
              {previaCsv.resumo?.sobras_no_sistema > 0 && (
                <Tag color="orange">sobram no sistema: {previaCsv.resumo.sobras_no_sistema}</Tag>
              )}
            </Space>
            <Text type="secondary" style={{ fontSize: 11 }}>
              As categorias vêm sugeridas pelo histórico (o que já foi classificado antes) e, no que
              for novidade, por IA. Confira e ajuste antes de criar — o que você confirmar aqui é o
              que o sistema usa para sugerir da próxima vez.
            </Text>
            <Table size="small" rowKey={(r: any) => `${r.data}_${r.descricao}_${r.valor}`}
              pagination={false} scroll={{ y: 320 }}
              dataSource={previaCsv.linhas || []}
              columns={[
                { title: 'Data', dataIndex: 'data', width: 92, render: (v: string) => fmtDataBr(v) },
                { title: 'Descrição', dataIndex: 'descricao', ellipsis: true },
                { title: 'Valor', dataIndex: 'valor', width: 96, align: 'right' as const,
                  render: (v: number) => fmt(v) },
                { title: 'Categoria', width: 260,
                  render: (_: any, r: any) => {
                    if (r.situacao !== 'criar') return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
                    const k = chaveLinha(r);
                    const valorAtual = catPorLinha[k] ?? r.categoria_sugerida ?? undefined;
                    const origem = catPorLinha[k] !== undefined ? 'voce' : r.categoria_origem;
                    const selo = origem === 'voce' ? { txt: 'você', cor: '#020D7F' }
                      : origem === 'memoria' ? { txt: `histórico ${r.categoria_memoria_vezes}×`, cor: '#0a7f4a' }
                      : origem === 'memoria_parcial' ? { txt: 'parecido', cor: '#0a7f4a' }
                      : origem === 'ia' ? { txt: `IA · ${r.categoria_confianca}`, cor: '#722ed1' }
                      : null;
                    return (
                      <div>
                        <Select size="small" style={{ width: '100%' }} value={valorAtual}
                          placeholder="classificar…" allowClear showSearch optionFilterProp="label"
                          status={!valorAtual ? 'warning' : undefined}
                          onChange={(v) => setCatPorLinha(m => ({ ...m, [k]: v }))}
                          options={categoriasDre
                            .filter((c: any) => c.categoria_contaazul && c.sinal !== 1)
                            .map((c: any) => ({
                              label: `${c.linha_dre}${c.grupo_dre ? ` (${c.grupo_dre})` : ''}`,
                              value: c.categoria_contaazul }))} />
                        {selo && (
                          <div style={{ fontSize: 10, color: selo.cor, marginTop: 1 }}>{selo.txt}</div>
                        )}
                      </div>
                    );
                  } },
                { title: 'Situação', width: 190,
                  render: (_: any, r: any) => r.situacao === 'criar'
                    ? <Tag color="blue" style={{ margin: 0 }}>vai criar</Tag>
                    : r.situacao === 'pagamento'
                      ? <Tag style={{ margin: 0 }}>pagamento — ignora</Tag>
                      : <Tag color="green" style={{ margin: 0 }} title={r.casada_com?.descricao}>
                          já existe: {r.casada_com?.descricao?.slice(0, 18)}
                        </Tag> },
              ] as any} />
            {(previaCsv.sobras || []).length > 0 && (
              <Card size="small" style={{ borderColor: '#ffd591', background: '#fffbe6' }}>
                <Text strong style={{ fontSize: 12 }}>
                  Já estão na fatura do sistema mas o CSV não os cobre — confira depois de importar
                </Text>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                  Típico: agendado do Conta Azul com valor estimado diferente do cobrado (assinatura
                  em dólar, seguro reajustado). Se o CSV criou o valor real, cancele o agendado no
                  Contas a Pagar — senão a fatura fica com os dois.
                </Text>
                {(previaCsv.sobras || []).map((x: any) => (
                  <div key={x.id} style={{ fontSize: 12 }}>
                    • {x.descricao} — <Text strong>{fmt(x.valor)}</Text>
                  </div>
                ))}
              </Card>
            )}
          </Space>
        )}
      </Modal>

      {/* Classificar pendentes — mesma revisão da prévia do import, só que
          sobre o que JÁ está na fatura sem categoria. */}
      <Modal open={modalClassif} onCancel={() => setModalClassif(false)}
        title={`Classificar pendentes — ${cartao?.nome || ''} · ${rotuloMes(mes)}`}
        width="94vw" style={{ maxWidth: 980, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
        footer={[
          <Button key="c" onClick={() => setModalClassif(false)}>Fechar</Button>,
          <Button key="ok" type="primary" loading={classificando}
            disabled={!classifLinhas.length} onClick={aplicarClassificacao}>
            Aplicar {classifLinhas.length ? `(${classifLinhas.length})` : ''}
          </Button>,
        ]}>
        {classificando && !classifLinhas.length ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : !classifLinhas.length ? (
          <Empty description="Nenhum lançamento sem categoria nesta fatura." />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={10}>
            <Space wrap>
              <Tag color="blue">sem categoria: {classifResumo?.pendentes}</Tag>
              {classifResumo?.sugeridas > 0 && (
                <Tag color="purple">com sugestão: {classifResumo.sugeridas}</Tag>
              )}
            </Space>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Sugestões vêm do histórico já classificado e, no que for novidade, de IA.
              Confira e ajuste — o que você aplicar aqui vira aprendizado para as próximas faturas.
            </Text>
            <Table size="small" rowKey="id" pagination={false} scroll={{ y: 380 }}
              dataSource={classifLinhas}
              columns={[
                { title: 'Data', dataIndex: 'data', width: 92, render: (v: string) => fmtDataBr(v) },
                { title: 'Descrição', dataIndex: 'descricao', ellipsis: true },
                { title: 'Valor', dataIndex: 'valor', width: 96, align: 'right' as const,
                  render: (v: any) => fmt(Math.abs(Number(v))) },
                { title: 'Categoria', width: 280,
                  render: (_: any, r: any) => {
                    const atual = classifCat[r.id] ?? r.categoria_sugerida ?? undefined;
                    const origem = classifCat[r.id] !== undefined ? 'voce' : r.categoria_origem;
                    const selo = origem === 'voce' ? { txt: 'você', cor: '#020D7F' }
                      : origem === 'memoria' ? { txt: `histórico ${r.categoria_memoria_vezes}×`, cor: '#0a7f4a' }
                      : origem === 'memoria_parcial' ? { txt: 'parecido', cor: '#0a7f4a' }
                      : origem === 'ia' ? { txt: `IA · ${r.categoria_confianca}`, cor: '#722ed1' }
                      : null;
                    return (
                      <div>
                        <Select size="small" style={{ width: '100%' }} value={atual}
                          placeholder="classificar…" allowClear showSearch optionFilterProp="label"
                          status={!atual ? 'warning' : undefined}
                          onChange={(v) => setClassifCat(m => ({ ...m, [r.id]: v }))}
                          options={categoriasDre
                            .filter((c: any) => c.categoria_contaazul && c.sinal !== 1)
                            .map((c: any) => ({
                              label: `${c.linha_dre}${c.grupo_dre ? ` (${c.grupo_dre})` : ''}`,
                              value: c.categoria_contaazul }))} />
                        {selo && <div style={{ fontSize: 10, color: selo.cor, marginTop: 1 }}>{selo.txt}</div>}
                      </div>
                    );
                  } },
              ] as any} />
          </Space>
        )}
      </Modal>
    </div>
  );
}

export default function FinanceiroCartoes() {
  return <AntApp><FinanceiroCartoesInner /></AntApp>;
}
